import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AppShell from '../ui/AppShell/AppShell';
import Button from '../ui/Button';
import Icon from '../ui/Icon';
import { ICONS } from '../ui/icon-map';
import ViewAsBanner from './ViewAsBanner';

/**
 * Page layout: every signed-in page renders inside the app shell with a consistent header.
 *
 * Props (unchanged for existing pages):
 *   title, subtitle, backPath (path string or function), showHome (ignored: the sidebar replaces it)
 * New:
 *   actions   - buttons shown on the right of the page header
 *   hideHeader - render the shell only (the page draws its own header)
 */
export default function Layout({ children, title, subtitle, backPath, actions, hideHeader = false }) {
    const navigate = useNavigate();

    useEffect(() => {
        if (typeof title === 'string' && title) {
            document.title = `${title} · Yelo Dubai`;
        }
    }, [title]);

    const goBack = () => {
        if (typeof backPath === 'function') backPath();
        else if (backPath) navigate(backPath);
    };

    const showHeader = !hideHeader && (title || subtitle || actions);

    return (
        <AppShell>
            {showHeader && (
                <header className="page-header">
                    <div className="page-header__main">
                        {backPath && (
                            <Button
                                variant="secondary"
                                size="sm"
                                iconOnly
                                className="page-header__back"
                                onClick={goBack}
                                aria-label="Back"
                                title="Back"
                                leadingIcon={<Icon glyph={ICONS.back} size="sm" />}
                            />
                        )}
                        <div className="page-header__text">
                            {title && <h1 className="page-header__title">{title}</h1>}
                            {subtitle && <div className="page-header__subtitle">{subtitle}</div>}
                        </div>
                    </div>
                    {actions && <div className="page-header__actions">{actions}</div>}
                </header>
            )}
            <ViewAsBanner />
            {children}
        </AppShell>
    );
}
